import styled, { useTheme } from 'styled-components'
import { useCallback, useRef, useState } from 'react'
import Decimal from 'decimal.js'
import ContentLoader from 'react-content-loader'

import { PhantomTypeArgument } from '@/shared/gen/_framework/reified'
import { CoinInfo } from '@kunalabs-io/kai'

import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { IconCheck } from 'components/Icons/IconCheck'
import { Menu } from 'components/Menu/Menu'
import { Typography } from 'components/Typography/Typography'
import { useOnClickOutside } from 'utils/onClickOutside'
import { useIsMobile } from 'utils/useIsMobile'
import { getFormattedUsdValue } from 'utils/usdFormatters'

const MenuContainer = styled.div<{ $disabled: boolean }>`
  position: relative;
  ${p => p.$disabled && `opacity: 0.5; pointer-events: none;`}
`

const MenuTrigger = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 8px;
  border-radius: 8px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  cursor: pointer;
  div {
    display: flex;
    align-items: center;
  }
`

const StyledMenuItem = styled.div`
  & > div {
    padding: 12px;
    display: flex;
    align-items: center;
    cursor: pointer;
    &:hover {
      background-color: ${p => p.theme.colors.background.hover};
    }
  }
`

interface MenuItemProps {
  item: CoinInfo<PhantomTypeArgument>
  activeOption: CoinInfo<PhantomTypeArgument> | null
  onClick: (o: CoinInfo<PhantomTypeArgument>) => void
}

const TotalValuesContentLoader = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={130}
      height={20}
      viewBox="0 0 130 20"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="0" rx="4" ry="4" width="130" height="20" />
    </ContentLoader>
  )
}

const MenuItem = ({ item, onClick, activeOption }: MenuItemProps) => {
  const isMobile = useIsMobile()
  return (
    <StyledMenuItem onClick={() => onClick(item)}>
      <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
        <img src={item.iconUrl} style={{ width: 24, height: 24, borderRadius: '50%' }} />
        <span style={{ marginLeft: 8 }}>{item.displaySymbol}</span>
        {item.typeName === activeOption?.typeName && (
          <div style={{ marginLeft: 'auto', display: 'flex' }}>
            <IconCheck />
          </div>
        )}
      </Typography>
    </StyledMenuItem>
  )
}

interface Props {
  isLoading: boolean
  disabled: boolean
  activeOption: CoinInfo<PhantomTypeArgument>
  onOptionClick: (o: CoinInfo<PhantomTypeArgument>) => void
  options: CoinInfo<PhantomTypeArgument>[]
  rewardsValue: Decimal
  rewardsValueDollars?: Decimal
}

export const ConvertRewardsToMenu = ({
  isLoading,
  activeOption,
  onOptionClick,
  options,
  disabled,
  rewardsValue,
  rewardsValueDollars,
}: Props) => {
  const isMobile = useIsMobile()

  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuNode = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutside(menuNode, onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  const handleOptionClick = useCallback(
    (o: CoinInfo<PhantomTypeArgument>) => {
      onOptionClick(o)
      toggleMenu()
    },
    [onOptionClick, toggleMenu]
  )

  return (
    <MenuContainer $disabled={disabled}>
      <MenuTrigger onClick={toggleMenu}>
        <Typography variant={isMobile ? 'caption' : 'description'} fontWeight="bold">
          {activeOption ? <img src={activeOption.iconUrl} style={{ width: 24, height: 24 }} /> : null}
          {activeOption ? <span style={{ marginLeft: 8 }}>{activeOption.displaySymbol}</span> : null}
        </Typography>
        {isLoading ? (
          <TotalValuesContentLoader />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="description" fontWeight="semiBold">
              {rewardsValue.toDP(8).toString()}
            </Typography>
            {typeof rewardsValueDollars !== 'undefined' ? (
              <Typography variant="caption" color="secondary" style={{ marginLeft: 4, marginRight: 4 }}>
                {`(${getFormattedUsdValue(rewardsValueDollars.toNumber())})`}
              </Typography>
            ) : null}
            <IconArrowDown width={20} height={20} />
          </div>
        )}
      </MenuTrigger>
      {isMenuOpen && (
        <Menu
          anchorElement={menuNode}
          style={{ top: -220, width: 'calc(100% - 2px)', padding: '8px 0', zIndex: 1, height: 250, overflowY: 'auto' }}
        >
          {options.map(o => (
            <MenuItem key={o.typeName} item={o} onClick={handleOptionClick} activeOption={activeOption} />
          ))}
        </Menu>
      )}
    </MenuContainer>
  )
}
